import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';

/**
 * @typedef {Object} Store
 * @property {string} handle - Unique store handle.
 * @property {string} [name] - The store name.
 * @property {string} [address_line_1] - The street address.
 * @property {string} [address_line_2] - Additional address line.
 * @property {string} [postal_code] - The postal/zip code.
 * @property {string} [city] - The city name.
 * @property {string} [country_code] - ISO 2-letter country code.
 * @property {number} [latitude] - The latitude coordinate.
 * @property {number} [longitude] - The longitude coordinate.
 * @property {string} [type] - Store type (e.g. 'brandstore', 'reseller').
 * @property {string} [phone] - The phone number.
 * @property {string} [image] - Image URL.
 * @property {string} [opening_hours] - Opening hours text (English fallback).
 * @property {OpeningHoursData} [opening_hours_data] - Structured weekly opening periods.
 * @property {boolean} [is_flagship] - Whether this is a flagship store.
 * @property {string} [_country_search] - Derived: lowercased country names (all store languages) for search.
 *
 * @typedef {Object} OpeningHoursPeriod
 * @property {{day: number, time: string}} open - day = 0-6 (Sunday = 0); time = "HHMM".
 * @property {{day: number, time: string}} close
 *
 * @typedef {Object} OpeningHoursData
 * @property {OpeningHoursPeriod[]} periods
 */

/**
 * APC Store Locator custom element that fetches store data from a JSON file
 * and renders store cards, map markers, search, and tag filters client-side.
 *
 * @fires store-locator:select - When a store is selected from the list or map
 *
 * @typedef {Object} ApcStoreLocatorRefs
 * @property {HTMLElement} listContainer - The list/grid container element.
 * @property {HTMLElement} [mapContainer] - The map container element.
 * @property {HTMLInputElement} [searchInput] - The search input element.
 * @property {HTMLElement} [liveRegion] - ARIA live region for announcements.
 * @property {HTMLElement} [emptyState] - Empty state message element.
 * @property {HTMLElement} [errorState] - Error state message element.
 * @property {HTMLElement} [filtersContainer] - Container for tag filter buttons.
 * @property {HTMLElement} [skeleton] - Skeleton loading state element.
 *
 * @extends {Component<ApcStoreLocatorRefs>}
 */
class ApcStoreLocator extends Component {
  requiredRefs = ['listContainer'];

  /** @type {AbortController} */
  #controller = new AbortController();

  /** @type {*} */
  #map = null;

  /** @type {*} Single shared popup for the currently selected pin. */
  #popup = null;

  /** @type {boolean} Whether the clustered source/layers have been added to the map. */
  #clusterLayersReady = false;

  /** @type {Store[]} */
  #stores = [];

  /** @type {Store[]} */
  #filteredStores = [];

  /** Locales used to make each store's country searchable by name (e.g. "France"/"Frankrijk"). */
  #countrySearchLocales = ['nl', 'en', 'fr', 'de'];

  /** @type {Map<string, string>} country_code → cached lowercased searchable names. */
  #countryTextCache = new Map();

  /** @type {IntersectionObserver | null} */
  #visibilityObserver = null;

  /** @type {HTMLElement[]} */
  #storeItems = [];

  /** @type {Set<string>} */
  #activeTypes = new Set();

  /** @type {Record<string, string>} */
  #storeNotes = {};

  /**
   * Gets the Mapbox GL JS library from window.
   * @returns {*}
   */
  get #mapboxgl() {
    return /** @type {*} */ (window).mapboxgl;
  }

  connectedCallback() {
    super.connectedCallback();

    try {
      this.#fetchStores();
    } catch (error) {
      console.warn('APC Store locator initialization failed:', error);
    }
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#visibilityObserver?.disconnect();
    this.#map?.remove();

    super.disconnectedCallback();
  }

  /**
   * Called when the section is re-rendered by the Section Rendering API.
   */
  updatedCallback() {
    try {
      // Refresh the clustered source/layers on the existing map (rather than
      // letting #fetchStores() re-trigger #initMap()) so live theme-editor
      // changes to cluster_radius/cluster_max_zoom take effect — Mapbox can't
      // change those options on an already-created GeoJSON source.
      if (this.#map) {
        this.#refreshClusterLayers();
      }
      this.#fetchStores();
    } catch (error) {
      console.warn('APC Store locator update failed:', error);
    }
  }

  /**
   * Fetches store data from the JSON file URL.
   */
  async #fetchStores() {
    const url = this.dataset.storeDataUrl;
    if (!url) {
      this.#hideSkeleton();
      return;
    }

    try {
      const response = await fetch(url, { signal: this.#controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      this.#stores = await response.json();
      this.#stores.forEach((store) => {
        store._country_search = this.#countrySearchText(store.country_code);
      });
      this.#filteredStores = [...this.#stores];

      try {
        this.#storeNotes = JSON.parse(this.dataset.storeNotes || '{}');
      } catch {
        // ignore
      }

      this.#renderCards();
      this.#buildFilters();
      this.#initSearch();
      this.#initStoreItems();
      this.#activateDefaultFilter();
      this.#hideSkeleton();
      this.#announceResults();

      if (this.#shouldShowMap() && !this.#map) {
        this.#observeVisibility();
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.warn('Failed to fetch store data:', error);
      this.#showError();
    }
  }

  // ---------------------------------------------------------------------------
  // Card rendering
  // ---------------------------------------------------------------------------

  /**
   * Renders store cards into the list container.
   */
  #renderCards() {
    const { listContainer } = this.refs;
    const interactive = this.#shouldShowMap();

    const html = this.#stores.map((store) => this.#cardHtml(store, interactive)).join('');

    listContainer.insertAdjacentHTML('afterbegin', html);
  }

  /**
   * Generates HTML for a single store card.
   * @param {Store} store - The store data.
   * @param {boolean} interactive - Whether the card should be interactive.
   * @returns {string} HTML string.
   */
  #cardHtml(store, interactive) {
    const name = this.#escapeHtml(store.name || '');
    const addr1 = this.#escapeHtml(store.address_line_1 || '');
    const addr2 = store.address_line_2 ? this.#escapeHtml(store.address_line_2) : '';
    const postal = this.#escapeHtml(store.postal_code || '');
    const city = this.#escapeHtml(store.city || '');
    const country = this.#escapeHtml((store.country_code || '').toUpperCase());
    const phone = store.phone ? this.#escapeHtml(store.phone) : '';
    const notes = this.#storeNotes[store.handle] || '';
    const storeType = (store.type || '').toLowerCase().replace(/\s+/g, '-');

    const image = store.image || '';
    const isBrandstore = storeType === 'brandstore';

    const periods = isBrandstore && store.opening_hours_data?.periods?.length ? store.opening_hours_data.periods : null;

    const status = isBrandstore
      ? periods
        ? this.#computeOpenStatusFromPeriods(periods)
        : this.#computeOpenStatus(store.opening_hours)
      : null;

    const statusDot = status
      ? `<span class="apc-store-locator__status-dot" data-open="${status === 'open'}" aria-hidden="true"></span>`
      : '';

    const hoursContent = isBrandstore
      ? periods
        ? this.#renderHoursFromPeriods(periods, status)
        : store.opening_hours
          ? this.#renderHoursLines(store.opening_hours, status)
          : ''
      : '';

    return `<article
      class="apc-store-locator__card${interactive ? ' apc-store-locator__card--interactive' : ''}"
      data-store-id="${this.#escapeHtml(store.handle)}"
      data-latitude="${store.latitude || ''}"
      data-longitude="${store.longitude || ''}"
      ${storeType ? `data-type="${storeType}"` : ''}
      ${interactive ? 'tabindex="0"' : ''}
      role="listitem"
    >
      ${image ? `<img class="apc-store-locator__card-image" src="${this.#escapeHtml(image)}" alt="${name}" loading="lazy" width="400" height="300">` : ''}
      <div class="apc-store-locator__card-content">
        <h3 class="apc-store-locator__card-name h5">${statusDot}${name}</h3>
        ${notes ? `<div class="apc-store-locator__card-notes">${notes}</div>` : ''}
        <div class="apc-store-locator__card-contact">
          <address class="apc-store-locator__card-address">
            ${addr1}${addr2 ? `<br>${addr2}` : ''}<br>${postal} ${city}${country ? `<br>${country}` : ''}
          </address>
          ${phone ? `<a href="tel:${phone.replace(/\s/g, '')}" class="apc-store-locator__card-phone">${phone}</a>` : ''}
        </div>
        ${
          hoursContent
            ? `<details-custom class="apc-store-locator__card-hours">
          <button class="apc-store-locator__card-hours-summary" type="button" ref="accordionSummary" aria-expanded="false">
            <span>${this.dataset.openingHoursLabel || 'Opening hours'}</span>
            <svg class="apc-store-locator__card-hours-icon" width="12" height="12" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M11 5.5L7 9.5L3 5.5" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <div class="apc-store-locator__card-hours-wrapper" ref="accordionContentWrapper" inert style="max-height: 0px;">
            <div class="apc-store-locator__card-hours-content" ref="accordionContent">${hoursContent}</div>
          </div>
        </details-custom>`
            : ''
        }
        ${isBrandstore ? `<a href="/pages/stores/${this.#escapeHtml(store.handle)}" class="apc-store-locator__card-detail-link button-link">${this.dataset.viewStoreLabel || 'View store'}</a>` : ''}
      </div>
    </article>`;
  }

  /**
   * Escapes HTML entities to prevent XSS.
   * @param {string} str - The string to escape.
   * @returns {string} Escaped string.
   */
  #escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * Parses a Google Places opening_hours string and returns the current open/closed status
   * for today, or null if today's hours can't be parsed.
   * @param {string | undefined} hoursString - Multi-line "Day: time-range" string.
   * @returns {'open' | 'closed' | null}
   */
  #computeOpenStatus(hoursString) {
    if (!hoursString) return null;
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const now = new Date();
    const today = dayNames[now.getDay()];
    const todayLine = hoursString.split('\n').find((l) => l.trim().startsWith(today + ':'));
    if (!todayLine) return null;
    const value = todayLine.slice(todayLine.indexOf(':') + 1).trim();
    if (/closed/i.test(value)) return 'closed';
    const nowMin = now.getHours() * 60 + now.getMinutes();
    const periods = value.split(',').map((p) => p.trim());
    for (const period of periods) {
      const [openStr, closeStr] = period.split(/[–—-]/).map((s) => s.trim());
      if (!openStr || !closeStr) continue;
      const closeMin = this.#parseTime12h(closeStr);
      if (closeMin == null) continue;
      const openHasMeridiem = /\b(AM|PM)\b/i.test(openStr);
      const inferred = openHasMeridiem ? '' : closeStr.match(/\b(AM|PM)\b/i)?.[1] || '';
      const openMin = this.#parseTime12h(openHasMeridiem ? openStr : `${openStr} ${inferred}`.trim());
      if (openMin == null) continue;
      if (nowMin >= openMin && nowMin < closeMin) return 'open';
    }
    return 'closed';
  }

  /**
   * Parses a 12h or 24h time string like "9:30 AM", "6 PM", "12:00", "18:00".
   * Returns minutes since midnight, or null if unparseable.
   * @param {string} str
   * @returns {number | null}
   */
  #parseTime12h(str) {
    if (!str) return null;
    const m = str.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?$/i);
    if (!m) return null;
    let h = parseInt(m[1], 10);
    const min = m[2] ? parseInt(m[2], 10) : 0;
    const mer = m[3]?.toUpperCase();
    if (mer === 'AM' && h === 12) h = 0;
    if (mer === 'PM' && h !== 12) h += 12;
    return h * 60 + min;
  }

  /**
   * Parses "HHMM" into minutes since midnight.
   * @param {string} time
   * @returns {number}
   */
  #periodTimeToMinutes(time) {
    const t = parseInt(time, 10);
    if (Number.isNaN(t)) return 0;
    return Math.floor(t / 100) * 60 + (t % 100);
  }

  /**
   * Computes today's open/closed status from structured periods.
   * @param {OpeningHoursPeriod[]} periods
   * @returns {'open' | 'closed' | null}
   */
  #computeOpenStatusFromPeriods(periods) {
    if (!periods?.length) return null;
    const now = new Date();
    const today = now.getDay();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    const todayPeriods = periods.filter((p) => p.open?.day === today);
    if (!todayPeriods.length) return 'closed';
    for (const p of todayPeriods) {
      const openMin = this.#periodTimeToMinutes(p.open.time);
      const closeMin = this.#periodTimeToMinutes(p.close.time);
      if (nowMin >= openMin && nowMin < closeMin) return 'open';
    }
    return 'closed';
  }

  /**
   * Renders the opening-hours block from structured periods, with localized
   * day names and 24h-formatted times. Marks today's row with a status dot.
   * @param {OpeningHoursPeriod[]} periods
   * @param {'open' | 'closed' | null} status
   * @returns {string} HTML
   */
  #renderHoursFromPeriods(periods, status) {
    /** @type {Record<string, string>} */
    let labels = {};
    try {
      labels = JSON.parse(this.dataset.weekdayTranslations || '{}');
    } catch {
      // ignore
    }
    const fallback = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const closedLabel = labels.closed || 'Closed';
    const today = new Date().getDay();
    // Monday-first display order; Google day index uses Sunday = 0.
    const displayOrder = [1, 2, 3, 4, 5, 6, 0];

    const formatTime = (timeStr) => {
      const t = parseInt(timeStr, 10);
      if (Number.isNaN(t)) return '';
      const h = Math.floor(t / 100);
      const m = t % 100;
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    };

    return displayOrder
      .map((dayIdx) => {
        const isToday = dayIdx === today;
        const dayLabel = labels[fallback[dayIdx]] || fallback[dayIdx];
        const dayPeriods = periods.filter((p) => p.open?.day === dayIdx);
        const hours = dayPeriods.length
          ? dayPeriods.map((p) => `${formatTime(p.open.time)} – ${formatTime(p.close.time)}`).join(', ')
          : closedLabel;
        const dotState = isToday && status ? status === 'open' : '';
        const dot = `<span class="apc-store-locator__status-dot"${dotState !== '' ? ` data-open="${dotState}"` : ''} aria-hidden="true"></span>`;
        return `<div class="apc-store-locator__card-hours-row${isToday ? ' is-today' : ''}">${dot}${this.#escapeHtml(`${dayLabel}: ${hours}`)}</div>`;
      })
      .join('');
  }

  /**
   * Renders the opening-hours block as one line per day, marking today's row
   * with a status dot (open/closed). Used as fallback when structured periods
   * aren't available — parses the English Google Places description string.
   * @param {string} hoursString
   * @param {'open' | 'closed' | null} status
   * @returns {string} HTML
   */
  #renderHoursLines(hoursString, status) {
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const today = dayNames[new Date().getDay()];
    return hoursString
      .split('\n')
      .map((line) => {
        const isToday = line.trim().startsWith(today + ':');
        const dotState = isToday && status ? status === 'open' : '';
        return `<div class="apc-store-locator__card-hours-row${isToday ? ' is-today' : ''}"><span class="apc-store-locator__status-dot"${dotState !== '' ? ` data-open="${dotState}"` : ''} aria-hidden="true"></span>${this.#escapeHtml(line)}</div>`;
      })
      .join('');
  }

  // ---------------------------------------------------------------------------
  // Filters
  // ---------------------------------------------------------------------------

  /**
   * Builds tag filter buttons from the store data.
   */
  #buildFilters() {
    const { filtersContainer } = this.refs;
    if (!filtersContainer) return;

    /** @type {Map<string, string>} handle → label */
    const typeMap = new Map();

    for (const store of this.#stores) {
      if (!store.type) continue;
      const handle = store.type.toLowerCase().replace(/\s+/g, '-');
      if (!typeMap.has(handle)) {
        typeMap.set(handle, store.type);
      }
    }

    if (typeMap.size === 0) return;

    /** @type {Record<string, string>} */
    let translations = {};
    try {
      translations = JSON.parse(this.dataset.filterTranslations || '{}');
    } catch {
      // ignore
    }

    const sorted = [...typeMap.entries()].sort(([a], [b]) => {
      if (a === 'brandstore') return -1;
      if (b === 'brandstore') return 1;
      return a.localeCompare(b);
    });

    filtersContainer.innerHTML = sorted
      .map(
        ([handle, label]) =>
          `<button type="button" class="pills__pill apc-store-locator__filter" data-type="${handle}" aria-pressed="false">${this.#escapeHtml(translations[handle] || label)}</button>`,
      )
      .join('');

    filtersContainer.hidden = false;
    this.#initFilters();
  }

  /**
   * Initializes tag filter button event listeners.
   */
  #initFilters() {
    const { filtersContainer } = this.refs;
    if (!filtersContainer) return;

    const filterButtons = filtersContainer.querySelectorAll('[data-type]');
    filterButtons.forEach((button) => {
      button.addEventListener('click', this.#handleFilterClick, {
        signal: this.#controller.signal,
      });
    });
  }

  /**
   * Activates the brandstore filter by default.
   */
  #activateDefaultFilter() {
    const { filtersContainer } = this.refs;
    if (!filtersContainer) return;

    const brandstoreButton = filtersContainer.querySelector('[data-type="brandstore"]');
    if (brandstoreButton) {
      brandstoreButton.setAttribute('aria-pressed', 'true');
      this.#activeTypes.add('brandstore');
      this.#applyFilters();
    }
  }

  /**
   * Handles filter button clicks.
   * @param {Event} event - The click event.
   */
  #handleFilterClick = (event) => {
    const button = /** @type {HTMLButtonElement} */ (event.currentTarget);
    const type = button.dataset.type;
    if (!type) return;

    const isPressed = button.getAttribute('aria-pressed') === 'true';

    // Deactivate all filters
    const { filtersContainer } = this.refs;
    if (filtersContainer) {
      filtersContainer.querySelectorAll('[data-type]').forEach((btn) => {
        btn.setAttribute('aria-pressed', 'false');
      });
    }
    this.#activeTypes.clear();

    // If it wasn't active, activate it
    if (!isPressed) {
      this.#activeTypes.add(type);
      button.setAttribute('aria-pressed', 'true');
    }

    this.#deselectAll();
    this.#applyFilters();
  };

  // ---------------------------------------------------------------------------
  // Search
  // ---------------------------------------------------------------------------

  /**
   * Initializes search input event listener.
   */
  #initSearch() {
    const { searchInput } = this.refs;
    if (searchInput) {
      searchInput.addEventListener('input', this.#handleSearch, {
        signal: this.#controller.signal,
      });
    }
  }

  /**
   * Handles search input to filter stores.
   */
  #handleSearch = debounce(() => {
    this.#applyFilters();
  }, 300);

  // ---------------------------------------------------------------------------
  // Filter logic
  // ---------------------------------------------------------------------------

  /**
   * Builds a lowercased searchable string of a country's names from its ISO code,
   * so a shopper can find resellers by country in any of the store's languages
   * (e.g. "FR" → "fr frankrijk france frankreich"). Falls back to the raw code.
   * @param {string} [code] - ISO 3166-1 alpha-2 country code.
   * @returns {string}
   */
  #countrySearchText(code) {
    if (!code) return '';

    const key = code.toUpperCase();
    const cached = this.#countryTextCache.get(key);
    if (cached !== undefined) return cached;

    const names = new Set([key.toLowerCase()]);
    for (const locale of this.#countrySearchLocales) {
      try {
        const name = new Intl.DisplayNames([locale], { type: 'region' }).of(key);
        if (name && name !== key) names.add(name.toLowerCase());
      } catch {
        // Ignore invalid codes/locales; the raw code stays searchable.
      }
    }

    const text = [...names].join(' ');
    this.#countryTextCache.set(key, text);
    return text;
  }

  /**
   * Applies both search and tag filters to the store list.
   */
  #applyFilters() {
    const { searchInput } = this.refs;
    const query = searchInput?.value.toLowerCase().trim() || '';

    this.#filteredStores = this.#stores.filter((store) => {
      const matchesSearch =
        !query ||
        store.name?.toLowerCase().includes(query) ||
        store.city?.toLowerCase().includes(query) ||
        store.address_line_1?.toLowerCase().includes(query) ||
        store.postal_code?.toLowerCase().includes(query) ||
        store._country_search?.includes(query);

      const matchesType =
        this.#activeTypes.size === 0 || this.#activeTypes.has((store.type || '').toLowerCase().replace(/\s+/g, '-'));

      return matchesSearch && matchesType;
    });

    const filteredHandles = new Set(this.#filteredStores.map((s) => s.handle));

    this.#updateStoreList(filteredHandles);
    this.#updateClusterData();
    this.#announceResults();
  }

  /**
   * Updates the visibility of store items in the list.
   * @param {Set<string>} filteredHandles - Set of visible store handles.
   */
  #updateStoreList(filteredHandles) {
    this.#storeItems.forEach((item) => {
      const handle = item.dataset.storeId || '';
      item.hidden = !filteredHandles.has(handle);
    });

    const { emptyState } = this.refs;
    if (emptyState) {
      emptyState.hidden = this.#filteredStores.length > 0;
    }
  }

  // ---------------------------------------------------------------------------
  // Map
  // ---------------------------------------------------------------------------

  /**
   * Whether the map should be shown.
   * @returns {boolean}
   */
  #shouldShowMap() {
    return Boolean(this.dataset.mapboxToken && this.refs.mapContainer);
  }

  /**
   * Observes when the map container becomes visible to lazy-load the map.
   */
  #observeVisibility() {
    const { mapContainer } = this.refs;
    if (!mapContainer) return;

    this.#visibilityObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          this.#initMap();
          this.#visibilityObserver?.disconnect();
        }
      },
      { threshold: 0.1 },
    );

    this.#visibilityObserver.observe(mapContainer);
  }

  /**
   * Resolves with the Mapbox GL JS library once it has loaded.
   *
   * Mapbox GL JS is loaded via a deferred external script, so it is not
   * guaranteed to be available when the map container first scrolls into view
   * (the container can win the race, notably in Safari). Poll until the library
   * is ready instead of giving up on the first miss.
   * @param {number} [timeout] - Max time to wait in ms.
   * @returns {Promise<*>} The mapboxgl global, or null if it never loads.
   */
  #whenMapboxReady(timeout = 10000) {
    if (this.#mapboxgl) return Promise.resolve(this.#mapboxgl);

    return new Promise((resolve) => {
      const start = performance.now();
      const interval = setInterval(() => {
        if (this.#mapboxgl) {
          clearInterval(interval);
          resolve(this.#mapboxgl);
        } else if (this.#controller.signal.aborted || performance.now() - start > timeout) {
          clearInterval(interval);
          resolve(null);
        }
      }, 50);
    });
  }

  /**
   * Initializes the Mapbox map.
   */
  async #initMap() {
    const token = this.dataset.mapboxToken;
    if (!token) return;

    const mapboxgl = await this.#whenMapboxReady();
    if (!mapboxgl) {
      console.warn('Mapbox GL JS failed to load');
      return;
    }

    try {
      mapboxgl.accessToken = token;

      this.#map = new mapboxgl.Map({
        container: this.refs.mapContainer,
        style: this.dataset.mapboxStyle || 'mapbox://styles/mapbox/light-v11',
        center: [4.9, 52.37],
        zoom: 6,
      });

      this.#map.addControl(new mapboxgl.NavigationControl());

      this.#map.on('error', (e) => {
        console.warn('Mapbox style error:', e.error);
      });

      this.#map.on('load', async () => {
        await this.#initClusterLayers();
        this.#updateClusterData();
        this.#fitToStores(this.#filteredStores);
      });

      this.#map.on('click', (e) => {
        // Deselect when clicking empty map area (not a cluster or pin).
        const features = this.#map.queryRenderedFeatures(e.point, {
          layers: ['clusters', 'unclustered-point'],
        });
        if (features.length === 0) {
          this.#deselectAll();
        }
      });
    } catch (error) {
      console.warn('Failed to initialize Mapbox map:', error);
    }
  }

  /**
   * Builds a GeoJSON FeatureCollection from stores with valid coordinates.
   * @param {Store[]} stores - Stores to convert.
   * @returns {*} GeoJSON FeatureCollection.
   */
  #buildFeatureCollection(stores) {
    return {
      type: 'FeatureCollection',
      features: stores
        .filter((store) => store.latitude && store.longitude)
        .map((store) => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [store.longitude, store.latitude] },
          properties: { handle: store.handle, type: store.type || '' },
        })),
    };
  }

  /**
   * Loads the primary/secondary pin images (merchant-uploaded or bundled
   * default) into the map's image sprite for use by the unclustered-point layer.
   *
   * Uses a plain `Image` element rather than `map.loadImage()`: Mapbox's own
   * loader fetches the file as a raw buffer and decodes it without an explicit
   * image MIME type, which fails for SVG (the default pin assets are SVG).
   * An `<img>` element respects the server's Content-Type header instead.
   * @returns {Promise<void>}
   */
  #loadPinImages() {
    const specs = [
      { name: 'pin-primary', url: this.dataset.pinImagePrimary || this.dataset.defaultPinPrimary },
      { name: 'pin-secondary', url: this.dataset.pinImageSecondary || this.dataset.defaultPinSecondary },
    ];

    return Promise.all(
      specs.map(
        ({ name, url }) =>
          new Promise((resolve) => {
            if (!url || this.#map.hasImage(name)) {
              resolve();
              return;
            }

            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              if (!this.#map.hasImage(name)) this.#map.addImage(name, img);
              resolve();
            };
            img.onerror = () => {
              console.warn(`Failed to load pin image "${name}" from ${url}`);
              resolve();
            };
            img.src = url;
          }),
      ),
    );
  }

  /**
   * Reads a resolved color-scheme hex value passed via a data attribute.
   *
   * Mapbox's paint properties require a legacy comma-separated color string
   * (e.g. "rgb(18,18,18)") or hex — its parser rejects the modern
   * space-separated "rgb(18 18 18 / 1.0)" syntax that this theme's
   * --color-foreground/--color-background CSS custom properties resolve to.
   * Reading a hex value rendered server-side via Liquid's `color_to_hex`
   * sidesteps that mismatch entirely.
   * @param {string} key - Dataset key (e.g. "clusterColor").
   * @param {string} fallback - Fallback color if the value is missing.
   * @returns {string}
   */
  #schemeColor(key, fallback) {
    return this.dataset[key] || fallback;
  }

  /**
   * Adds the clustered GeoJSON source and cluster/point layers to the map.
   */
  async #initClusterLayers() {
    if (!this.#map || this.#clusterLayersReady) return;

    try {
      await this.#loadPinImages();
      this.#addClusterLayers();
      this.#clusterLayersReady = true;
    } catch (error) {
      console.warn('Failed to initialize store clustering:', error);
    }
  }

  /**
   * Tears down and recreates the clustered source/layers on an already-initialized
   * map. Mapbox can't change clusterRadius/clusterMaxZoom on an existing GeoJSON
   * source, so this is what makes theme-editor edits to those settings apply live.
   */
  #refreshClusterLayers() {
    if (!this.#map || !this.#clusterLayersReady) return;

    ['clusters', 'cluster-count', 'unclustered-point'].forEach((id) => {
      if (this.#map.getLayer(id)) this.#map.removeLayer(id);
    });
    if (this.#map.getSource('stores')) this.#map.removeSource('stores');

    this.#clusterLayersReady = false;

    try {
      this.#addClusterLayers();
      this.#clusterLayersReady = true;
    } catch (error) {
      console.warn('Failed to refresh store clustering:', error);
    }
  }

  /**
   * Adds the clustered GeoJSON source, its layers, and their event handlers.
   */
  #addClusterLayers() {
    const clusterRadius = Number(this.dataset.clusterRadius) || 50;
    const clusterMaxZoom = Number(this.dataset.clusterMaxZoom) || 14;

    const darkColor = this.#schemeColor('clusterColor', '#1a1a1a');
    const lightColor = this.#schemeColor('clusterCountColor', '#fff');
    // A cluster feature doesn't inherit its points' "type" property by
    // default — clusterProperties tags each cluster with how many of its
    // points are brandstores, so the circle can match whichever pin style
    // (solid dark brandstore vs. light-with-border reseller) it's grouping.
    const isBrandstoreCluster = ['>', ['get', 'brandstore_count'], 0];

    this.#map.addSource('stores', {
      type: 'geojson',
      data: this.#buildFeatureCollection(this.#filteredStores),
      cluster: true,
      clusterRadius,
      clusterMaxZoom,
      clusterProperties: {
        brandstore_count: ['+', ['case', ['==', ['get', 'type'], 'brandstore'], 1, 0]],
      },
    });

    this.#map.addLayer({
      id: 'clusters',
      type: 'circle',
      source: 'stores',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': ['case', isBrandstoreCluster, darkColor, lightColor],
        'circle-stroke-color': darkColor,
        'circle-stroke-width': ['case', isBrandstoreCluster, 0, 1.5],
        'circle-radius': ['step', ['get', 'point_count'], 16, 25, 20, 100, 24],
      },
    });

    this.#map.addLayer({
      id: 'cluster-count',
      type: 'symbol',
      source: 'stores',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': ['get', 'point_count_abbreviated'],
        'text-size': 12,
      },
      paint: {
        'text-color': ['case', isBrandstoreCluster, lightColor, darkColor],
        'text-halo-color': ['case', isBrandstoreCluster, darkColor, lightColor],
        'text-halo-width': 1,
      },
    });

    this.#map.addLayer({
      id: 'unclustered-point',
      type: 'symbol',
      source: 'stores',
      filter: ['!', ['has', 'point_count']],
      layout: {
        'icon-image': ['case', ['==', ['get', 'type'], 'brandstore'], 'pin-primary', 'pin-secondary'],
        'icon-anchor': 'bottom',
        'icon-allow-overlap': true,
      },
    });

    this.#map.on('click', 'clusters', (e) => {
      const [feature] = this.#map.queryRenderedFeatures(e.point, { layers: ['clusters'] });
      const clusterId = feature?.properties?.cluster_id;
      if (clusterId == null) return;

      this.#map.getSource('stores').getClusterExpansionZoom(clusterId, (error, zoom) => {
        if (error) return;
        this.#map.easeTo({ center: feature.geometry.coordinates, zoom });
      });
    });

    this.#map.on('click', 'unclustered-point', (e) => {
      const handle = e.features?.[0]?.properties?.handle;
      if (handle) this.#selectStore(handle, 'map');
    });

    this.#map.on('mouseenter', 'clusters', () => {
      this.#map.getCanvas().style.cursor = 'pointer';
    });
    this.#map.on('mouseenter', 'unclustered-point', () => {
      this.#map.getCanvas().style.cursor = 'pointer';
    });
    this.#map.on('mouseleave', 'clusters', () => {
      this.#map.getCanvas().style.cursor = '';
    });
    this.#map.on('mouseleave', 'unclustered-point', () => {
      this.#map.getCanvas().style.cursor = '';
    });
  }

  /**
   * Fits the map bounds to show the given stores.
   * @param {Store[]} stores - Stores to fit.
   */
  #fitToStores(stores) {
    const mapboxgl = this.#mapboxgl;
    if (!this.#map || !mapboxgl) return;

    const validStores = stores.filter((s) => s.latitude && s.longitude);
    if (validStores.length === 0) return;

    try {
      const bounds = new mapboxgl.LngLatBounds();

      for (const store of validStores) {
        bounds.extend([store.longitude, store.latitude]);
      }

      this.#map.fitBounds(bounds, {
        padding: 50,
        maxZoom: 12,
        duration: prefersReducedMotion() ? 0 : 1000,
      });
    } catch (error) {
      console.warn('Failed to fit map bounds:', error);
    }
  }

  /**
   * Updates the clustered source's data to reflect the active filter/search,
   * so cluster counts always match the visible subset of stores.
   */
  #updateClusterData() {
    if (!this.#map || !this.#clusterLayersReady) return;

    this.#map.getSource('stores')?.setData(this.#buildFeatureCollection(this.#filteredStores));

    if (this.#filteredStores.length > 0) {
      this.#fitToStores(this.#filteredStores);
    }
  }

  // ---------------------------------------------------------------------------
  // Store selection
  // ---------------------------------------------------------------------------

  /**
   * Initializes store items and click handlers (only when map is enabled).
   */
  #initStoreItems() {
    this.#storeItems = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-store-id]')]);

    if (!this.#shouldShowMap()) return;

    this.#storeItems.forEach((item) => {
      item.addEventListener(
        'click',
        (e) => {
          const target = /** @type {HTMLElement} */ (e.target);
          if (target.closest('.apc-store-locator__card-hours')) return;
          this.#selectStore(item.dataset.storeId || '');
        },
        { signal: this.#controller.signal },
      );

      item.addEventListener(
        'keydown',
        (e) => {
          const event = /** @type {KeyboardEvent} */ (e);
          const target = /** @type {HTMLElement} */ (event.target);
          if (target.closest('.apc-store-locator__card-hours')) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.#selectStore(item.dataset.storeId || '');
          }
        },
        { signal: this.#controller.signal },
      );
    });
  }

  /**
   * Selects a store and flies to it on the map.
   * @param {string} storeHandle - The store handle to select.
   * @param {'list' | 'map'} source - Where the selection originated.
   */
  #selectStore(storeHandle, source = 'list') {
    let selectedItem = null;
    const currentlySelected = this.querySelector('[aria-selected="true"]');
    const isDeselect = currentlySelected?.dataset.storeId === storeHandle;

    this.#storeItems.forEach((item) => {
      const isSelected = !isDeselect && item.dataset.storeId === storeHandle;
      item.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      if (isSelected) selectedItem = item;
    });

    if (isDeselect) {
      this.#popup?.remove();
      return;
    }

    const scrollBehavior = prefersReducedMotion() ? 'instant' : 'smooth';

    if (source === 'map' && selectedItem) {
      // Marker clicked: on desktop, scroll within the list panel.
      // On mobile, don't scroll — the popup on the map is enough.
      const { listContainer } = this.refs;
      if (listContainer && listContainer.scrollHeight > listContainer.clientHeight) {
        selectedItem.scrollIntoView({ behavior: scrollBehavior, block: 'nearest' });
      }
    }

    if (source === 'list' && this.refs.mapContainer) {
      // Card clicked: scroll the map into view so the user sees the flyTo.
      this.refs.mapContainer.scrollIntoView({ behavior: scrollBehavior, block: 'nearest' });
    }

    // Close any open popup
    this.#popup?.remove();

    const store = this.#stores.find((s) => s.handle === storeHandle);
    if (store && this.#map && store.latitude && store.longitude) {
      this.#map.flyTo({
        center: [store.longitude, store.latitude],
        zoom: 14,
        duration: prefersReducedMotion() ? 0 : 1500,
      });

      this.#popup = new this.#mapboxgl.Popup({ offset: 25, className: 'apc-store-locator__popup' })
        .setLngLat([store.longitude, store.latitude])
        .setHTML(this.#popupHtml(store))
        .addTo(this.#map);
    }

    this.dispatchEvent(
      new CustomEvent('store-locator:select', {
        bubbles: true,
        detail: { store },
      }),
    );
  }

  /**
   * Deselects all store cards.
   */
  #deselectAll() {
    this.#storeItems.forEach((item) => {
      item.setAttribute('aria-selected', 'false');
    });
    this.#popup?.remove();
  }

  /**
   * Builds the popup HTML for a store's map pin.
   * @param {Store} store - The store data.
   * @returns {string} HTML string.
   */
  #popupHtml(store) {
    const storeNote = this.#storeNotes[store.handle] || '';

    return `
      <strong>${this.#escapeHtml(store.name || '')}</strong>
      ${storeNote ? `<div class="apc-store-locator__popup-notes">${storeNote}</div>` : ''}
      <div>${this.#escapeHtml(store.address_line_1 || '')}</div>
      <div>${this.#escapeHtml(store.postal_code || '')} ${this.#escapeHtml(store.city || '')}</div>
      ${store.phone ? `<a href="tel:${this.#escapeHtml(store.phone)}">${this.#escapeHtml(store.phone)}</a>` : ''}
    `;
  }

  // ---------------------------------------------------------------------------
  // Announcements
  // ---------------------------------------------------------------------------

  /**
   * Announces search results to screen readers.
   */
  #announceResults() {
    const { liveRegion } = this.refs;
    if (!liveRegion) return;

    const count = this.#filteredStores.length;

    if (count === 0) {
      liveRegion.textContent = this.dataset.noStoresFound || 'No stores found';
    } else {
      const template = count === 1 ? this.dataset.storesFoundOne : this.dataset.storesFoundOther;
      liveRegion.textContent = template ? template.replace('__count__', String(count)) : `${count} stores found`;
    }
  }

  // ---------------------------------------------------------------------------
  // State management
  // ---------------------------------------------------------------------------

  /**
   * Hides the skeleton loading state.
   */
  #hideSkeleton() {
    const { skeleton } = this.refs;
    if (skeleton) skeleton.hidden = true;
  }

  /**
   * Shows the error state.
   */
  #showError() {
    this.#hideSkeleton();
    const { errorState } = this.refs;
    if (errorState) errorState.hidden = false;
  }
}

if (!customElements.get('apc-store-locator')) {
  customElements.define('apc-store-locator', ApcStoreLocator);
}
